'use client';

import React, { useEffect, useState, useCallback } from 'react';
import { empresaService, AsignacionAsesorResponse, AsignacionEmpresaItem } from '@/lib/api/empresaService';
import { getCurrentUser } from '@/services/authService';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import ErrorMessage from '@/components/common/ErrorMessage';

/**
 * Módulo Asignaciones:
 * - Muestra lista de asesores activos
 * - Para cada asesor, muestra empresas de su región
 * - Permite asignar/desasignar empresas mediante checkboxes
 * - Similar al legacy: vista centrada en asesores
 */
const AsignacionesManagement: React.FC = () => {
  const [asignaciones, setAsignaciones] = useState<AsignacionAsesorResponse[]>([]);
  const [expandedAsesores, setExpandedAsesores] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAsignaciones = useCallback(async () => {
    setLoading(true);
    setError(null);
    const currentUser = getCurrentUser();
    if (!currentUser || !currentUser.ownerID) {
      setError("No se pudo obtener el ID del usuario actual.");
      setLoading(false);
      return;
    }
    try {
      const response = await empresaService.getAsignacionesView(currentUser.ownerID);
      if (response.data) {
        setAsignaciones(response.data);
    } else {
        setError(response.error || "Error al cargar asignaciones.");
    }
    } catch (err: any) {
      setError(err.message || "Error de red al cargar asignaciones.");
    } finally {
    setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAsignaciones();
  }, [fetchAsignaciones]);

  const toggleAsesor = (ownerID: string) => {
    setExpandedAsesores(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerID)) {
        newSet.delete(ownerID);
      } else {
        newSet.add(ownerID);
      }
      return newSet;
    });
  };

  const toggleAsignacion = async (empresaId: string, asesorOwnerID: string, enabled: boolean) => {
    // Actualizar estado local inmediatamente (optimistic update)
    setAsignaciones(prev =>
      prev.map(asesor => {
        if (asesor.ownerID !== asesorOwnerID) return asesor;
        return {
          ...asesor,
          empresas: asesor.empresas.map(emp =>
            emp.id === empresaId ? { ...emp, asignada: enabled } : emp
          ),
        };
      })
    );

    try {
      const response = await empresaService.setAsignacion(empresaId, asesorOwnerID, enabled);
      if (!response.data) {
        // Revertir si falla
        setAsignaciones(prev =>
          prev.map(asesor => {
            if (asesor.ownerID !== asesorOwnerID) return asesor;
            return {
              ...asesor,
              empresas: asesor.empresas.map(emp =>
                emp.id === empresaId ? { ...emp, asignada: !enabled } : emp
              ),
            };
          })
        );
        setError(response.error || "No se pudo actualizar la asignación.");
      }
    } catch (err: any) {
      // Revertir si hay error de red
      setAsignaciones(prev =>
        prev.map(asesor => {
          if (asesor.ownerID !== asesorOwnerID) return asesor;
          return {
            ...asesor,
            empresas: asesor.empresas.map(emp =>
              emp.id === empresaId ? { ...emp, asignada: !enabled } : emp
            ),
          };
        })
      );
      setError(err.message || "Error al actualizar asignación.");
    }
  };

  const isEmpresaAsignada = (empresaId: string, asesorOwnerID: string): boolean => {
    const asesor = asignaciones.find(a => a.ownerID === asesorOwnerID);
    if (!asesor) return false;
    
    // El backend ya incluye el campo 'asignada' en cada empresa
    const empresa = asesor.empresas.find(e => e.id === empresaId);
    return empresa?.asignada || false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Asignaciones</h1>
          <p className="text-sm text-gray-500 mt-1">Asignación de empresas a asesores por región.</p>
        </div>
        <Button variant="secondary" onClick={fetchAsignaciones}>Recargar</Button>
      </div>

      {error && <ErrorMessage message={error} />}

      <div className="space-y-4">
        {asignaciones.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
            <p className="text-gray-500">No hay asesores disponibles.</p>
          </div>
        ) : (
          asignaciones.map((asesor) => (
            <div
              key={asesor.ownerID}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Header del asesor (siempre visible) */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleAsesor(asesor.ownerID)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{asesor.asesor}</h3>
                    <p className="text-sm text-gray-500">ID: {asesor.ownerID}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">
                      {asesor.empresas.length} empresa{asesor.empresas.length !== 1 ? 's' : ''}
                    </span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedAsesores.has(asesor.ownerID) ? 'transform rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Lista de empresas (expandible) */}
              {expandedAsesores.has(asesor.ownerID) && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  {asesor.empresas.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">
                      No hay empresas disponibles en esta región.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {asesor.empresas.map((empresa) => {
                        const isAsignada = isEmpresaAsignada(empresa.id, asesor.ownerID);
                        return (
                          <div
                            key={empresa.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{empresa.nombre}</p>
                              <p className="text-xs text-gray-500">ID: {empresa.id}</p>
                            </div>
                            <div className="ml-4">
                  <Switch
                                checked={isAsignada}
                                onChange={(checked) => toggleAsignacion(empresa.id, asesor.ownerID, checked)}
                    label=""
                  />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
          )}
      </div>
    </div>
  );
};

export default AsignacionesManagement;
